import is from '@sindresorhus/is';
import { type ChangeEvent, createContext, type KeyboardEvent, useContext } from 'react';

import type { ChatContext } from './chat-context-types';
import type { ChatConversation } from './hooks/use-chat-conversation';
import type { UseChatTranscriptionReturn } from './hooks/use-chat-transcription';

export type ChatComposerCallbacks = {
    readonly close: () => void;
    /** Reveals the collapsed pill's topSlot without expanding the full chat. */
    readonly expandCollapsedComposer: () => void;
    readonly handleDraftChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    readonly handleKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
    readonly hasComposerAppeared: boolean;
    readonly isComposerEnabled: boolean;
    readonly isComposerReady: boolean;
    readonly markComposerAppeared: () => void;
    readonly open: () => void;
    readonly registerChatContext: (tileId: string, context: ChatContext) => void;
    readonly sendMessage: (text: string) => void;
    readonly setCollapsedComposerOpen: (open: boolean) => void;
    readonly setHoveredTile: (id: string | null) => void;
    readonly submit: () => void;
    readonly unregisterChatContext: (tileId: string) => void;
};

export type ChatComposerActive = {
    readonly activeChatContext: ChatContext | null;
    readonly activeChatContexts: readonly ChatContext[];
    /** Precedence: chat dock (if expanded) > hovered tile (if alive) > right-most open sidepanel > main tile. */
    readonly activeTileId: string;
    /** Same as activeTileId but ignores chat-expanded short-circuit. */
    readonly anchorTileId: string;
    /** Increments every time a consumer requests the collapsed pill to expand. */
    readonly collapsedComposerExpandSignal: number;
    readonly isCollapsedComposerOpen: boolean;
    readonly isExpanded: boolean;
    /** True when the surface is left of its rightmost rest position (chat expanded). Drives the chat card lift. */
    readonly isChatScrolled: boolean;
    /** True when a tile has crossed the inter-tile gap and is under the (sticky) chat. Drives tile recede. */
    readonly isContentUnderChat: boolean;
    /** Preferred single-context fallback for UI that can only render one context at a time. */
    readonly resolvedChatContext: ChatContext | null;
};

export type ChatComposerInput = {
    readonly draft: string;
    readonly transcription: UseChatTranscriptionReturn;
};

export const ChatComposerCallbacksContext = createContext<ChatComposerCallbacks | null>(null);
export const ChatComposerActiveContext = createContext<ChatComposerActive | null>(null);
export const ChatComposerInputContext = createContext<ChatComposerInput | null>(null);
export const ChatComposerConversationContext = createContext<ChatConversation | null>(null);

export const useChatComposerCallbacks = (): ChatComposerCallbacks => {
    const value = useContext(ChatComposerCallbacksContext);
    if (is.null(value)) {
        throw new Error('useChatComposerCallbacks must be used within a ChatComposerProvider');
    }
    return value;
};

export const useChatComposerActive = (): ChatComposerActive => {
    const value = useContext(ChatComposerActiveContext);
    if (is.null(value)) {
        throw new Error('useChatComposerActive must be used within a ChatComposerProvider');
    }
    return value;
};

export const useChatComposerInput = (): ChatComposerInput => {
    const value = useContext(ChatComposerInputContext);
    if (is.null(value)) {
        throw new Error('useChatComposerInput must be used within a ChatComposerProvider');
    }
    return value;
};

export const useChatComposerConversation = (): ChatConversation => {
    const value = useContext(ChatComposerConversationContext);
    if (is.null(value)) {
        throw new Error('useChatComposerConversation must be used within a ChatComposerProvider');
    }
    return value;
};
